import { IAnalytics, IAnalyticsContext } from '../models/helpers/AnalyticsHelper';
import { IGtmDataLayer } from '../models/helpers/AnalyticsHelper/GtmDataLayer/IGtmDataLayer';
import { IAnalyticsOptions } from '../models/helpers/AnalyticsHelper/IAnalyticsOptions';
import { AnalyticsType } from '../models/helpers/AnalyticsHelper/AnalyticsType';
import { ITealium } from '../models/helpers/AnalyticsHelper/Tealium/ITealium';
import { IUTag } from '../models/helpers/AnalyticsHelper/Tealium/IUTag';
import { TealiumHelper } from './TealiumHelper';

// Teach Typescript that there can be a datalayer on the window
declare global {
	interface Window {
		dataLayer?: IGtmDataLayer[];
		utag?: IUTag;
	}

}

export class AnalyticsHelper<T extends IAnalyticsContext> {
	analyticsType: AnalyticsType;
	tealiumHelper: TealiumHelper;
	constructor(public initDefaultContext: () => T, options?: IAnalyticsOptions) {
		this.analyticsType = options?.type || 'gtm';

		if (this.analyticsType === 'gtm' && !window.dataLayer) {
			window.dataLayer = [];
		}

		if (this.analyticsType === 'tealium') {
			this.tealiumHelper = new TealiumHelper();
		}
	}

	private loadDefaultContext(data: IAnalytics<Partial<T>>) {
		if (data && data.context) {
			data.context.timestamp = Date.now();
			data.context = Object.assign(data.context, this.initDefaultContext());
		}
	}

	private logGTMEvent(data: IAnalytics<Partial<T>>) {
		window.dataLayer.push(this.orchestrateGTMData(data));
	}

	private orchestrateGTMData(data: IAnalytics<Partial<T>>) {
		const gtmData: IGtmDataLayer = {
			event: data.event,
			eventType: data.eventType,
			context: data.context,
		};

		return gtmData;
	}

	private logTealiumEvent(data: IAnalytics<Partial<T>>) {
		if (window.utag) {
			window.utag.link(this.orchestrateTealiumData(data));
		}
		else {
			console.warn('Tealium script has not being embedded. Please see the following URL: https://docs.tealium.com/platforms/javascript/install/#universal-tag-utag-js');
		}
	}

	private orchestrateTealiumData(data: IAnalytics<Partial<T>>) {
		const tealiumData: ITealium = {
			tealium_event: data.event,
			...data.context,
		};

		const flattenedTealiumData = this.tealiumHelper.flattenObject(tealiumData);

		return flattenedTealiumData;
	}

	logEvent(data: IAnalytics<Partial<T>>): void {
		this.loadDefaultContext(data);

		switch (this.analyticsType) {
			case 'gtm':
				this.logGTMEvent(data);
				break;
			case 'tealium':
				this.logTealiumEvent(data);
				break;
			default:
				console.warn('No analyticsType has been configured');
				break;
		}
	}

}
